import { Routes } from '@angular/router';
import { authGuard } from './core/auth/auth.guard';
import { AdminRole } from './core/auth/models/admin-user-info';
import { roleGuard } from './core/auth/role.guard';
import { countryFeatureGuard } from './core/country/country-feature.guard';
import { countryQueryParamGuard } from './core/country/country-query-param.guard';
import { dynEnvOnlyGuard } from './core/country/dyn-env-only.guard';

export const routes: Routes = [
    {
        path: 'login',
        loadComponent: () => import('./pages/login/login.component').then(m => m.LoginComponent),
    },
    {
        path: '',
        canActivate: [authGuard],
        // A child guard, not a second `canActivate`: the router finishes the shell's `authGuard`
        // before it, so a `?country=` deep link is applied only once the token has narrowed the
        // allowed countries (see the guard).
        canActivateChild: [countryQueryParamGuard],
        children: [
            {
                path: '',
                redirectTo: 'dashboard',
                pathMatch: 'full',
            },
            {
                path: 'dashboard',
                loadComponent: () => import('./pages/dashboard/dashboard.component').then(m => m.DashboardComponent),
            },
            {
                path: 'customers',
                canActivate: [roleGuard(AdminRole.Admin, AdminRole.Moderator)],
                loadChildren: () => import('./pages/customers/customers.routes'),
            },
            {
                path: 'subscriptions',
                loadChildren: () => import('./pages/subscriptions/subscriptions.routes'),
            },
            {
                path: 'payments',
                loadChildren: () => import('./pages/payments/payments.routes'),
            },
            {
                path: 'tariffs',
                canActivate: [roleGuard(AdminRole.Admin, AdminRole.Moderator)],
                loadChildren: () => import('./pages/tariffs/tariffs.routes'),
            },
            {
                path: 'promos',
                loadChildren: () => import('./pages/promos/promos.routes'),
            },
            {
                path: 'bonuses',
                canActivate: [roleGuard(AdminRole.Admin, AdminRole.Moderator)],
                loadChildren: () => import('./pages/bonuses/bonuses.routes'),
            },
            {
                path: 'disputes',
                canActivate: [roleGuard(AdminRole.Admin, AdminRole.Moderator)],
                loadChildren: () => import('./pages/disputes/disputes.routes'),
            },
            {
                path: 'users',
                // SuperAdmin only: handing out roles and country access is the one action that can
                // widen anyone's reach. The guard admits SuperAdmin through its superset rule, so
                // naming the role here is purely so the route reads as what it grants.
                canActivate: [roleGuard(AdminRole.SuperAdmin)],
                loadChildren: () => import('./pages/users/users.routes'),
            },
            {
                path: 'scheduler',
                canActivate: [roleGuard(AdminRole.Admin)],
                loadChildren: () => import('./pages/scheduler/scheduler.routes'),
            },
            {
                path: 'notifications',
                canActivate: [roleGuard(AdminRole.Admin, AdminRole.Moderator)],
                loadChildren: () => import('./pages/notifications/notifications.routes'),
            },
            {
                path: 'geo',
                canActivate: [roleGuard(AdminRole.Admin, AdminRole.Moderator)],
                loadChildren: () => import('./pages/geo/geo.routes'),
            },
            {
                path: 'blacklists',
                canActivate: [roleGuard(AdminRole.Admin, AdminRole.Moderator)],
                loadChildren: () => import('./pages/blacklists/blacklists.routes'),
            },
            {
                path: 'referrals',
                canActivate: [roleGuard(AdminRole.Admin, AdminRole.Moderator)],
                loadChildren: () => import('./pages/referrals/referrals.routes'),
            },
            {
                path: 'unmanaged-ordering',
                canActivate: [roleGuard(AdminRole.Admin, AdminRole.Moderator), countryFeatureGuard('unmanagedOrdering')],
                loadChildren: () => import('./pages/unmanaged-ordering/unmanaged-ordering.routes'),
            },
            // The six payment-provider pages. Admin-only: the legacy Backoffice controllers they port
            // excluded Moderator, and the monolith endpoints behind them carry `AdminOnly`. The
            // YooKassa and Platron four are additionally RU-gated (`russianPaymentProviders`);
            // Stripe settles in every country, so its two are not.
            {
                path: 'yookassa-event-logs',
                canActivate: [roleGuard(AdminRole.Admin), countryFeatureGuard('russianPaymentProviders')],
                loadChildren: () => import('./pages/yookassa-event-logs/yookassa-event-logs.routes'),
            },
            {
                path: 'yookassa-rejected-payments',
                canActivate: [roleGuard(AdminRole.Admin), countryFeatureGuard('russianPaymentProviders')],
                loadChildren: () => import('./pages/yookassa-rejected-payments/yookassa-rejected-payments.routes'),
            },
            {
                path: 'platron-logs',
                canActivate: [roleGuard(AdminRole.Admin), countryFeatureGuard('russianPaymentProviders')],
                loadChildren: () => import('./pages/platron-logs/platron-logs.routes'),
            },
            {
                path: 'platron-rejected-payments',
                canActivate: [roleGuard(AdminRole.Admin), countryFeatureGuard('russianPaymentProviders')],
                loadChildren: () => import('./pages/platron-rejected-payments/platron-rejected-payments.routes'),
            },
            {
                path: 'stripe-events',
                canActivate: [roleGuard(AdminRole.Admin)],
                loadChildren: () => import('./pages/stripe-events/stripe-events.routes'),
            },
            {
                path: 'stripe-rejected-payments',
                canActivate: [roleGuard(AdminRole.Admin)],
                loadChildren: () => import('./pages/stripe-rejected-payments/stripe-rejected-payments.routes'),
            },
            {
                path: 'settings',
                canActivate: [roleGuard(AdminRole.Admin)],
                loadChildren: () => import('./pages/settings/settings.routes'),
            },
            {
                // Bootstrap jobs for a per-MR dynamic env (search reindex, postal-code indexing,
                // user sync). Admin-gated like the Scheduler whose trigger endpoint it drives, and
                // bounced off staging and production by the environment guard.
                path: 'environment-tools',
                canActivate: [roleGuard(AdminRole.Admin), dynEnvOnlyGuard()],
                loadChildren: () => import('./pages/environment-tools/environment-tools.routes'),
            },
            {
                path: 'seo',
                canActivate: [roleGuard(AdminRole.Admin, AdminRole.Moderator)],
                loadChildren: () => import('./pages/seo/seo.routes'),
            },
            {
                // The parent admits every role the block serves; children narrow from there, because the
                // block spans pages with different audiences — reordering catalog dictionaries is not a
                // translation task.
                path: 'localization',
                canActivate: [roleGuard(AdminRole.Admin, AdminRole.Moderator, AdminRole.Translator)],
                loadChildren: () => import('./pages/localization/localization.routes'),
            },
            {
                path: 'support-knowledge',
                loadChildren: () => import('./pages/support-knowledge/support-knowledge.routes'),
            },
            {
                path: 'ai-conversations',
                loadChildren: () => import('./pages/ai-conversations/ai-conversations.routes'),
            },
            {
                path: 'chats',
                canActivate: [roleGuard(AdminRole.Admin, AdminRole.Moderator)],
                loadChildren: () => import('./pages/chats/chats.routes'),
            },
            {
                path: 'ai-search-requests',
                loadChildren: () => import('./pages/ai-search-requests/ai-search-requests.routes'),
            },
            {
                path: 'ai-search-analytics',
                loadChildren: () => import('./pages/ai-search-analytics/ai-search-analytics.routes'),
            },
            {
                path: 'ai-support-analytics',
                loadChildren: () => import('./pages/ai-support-analytics/ai-support-analytics.routes'),
            },
            {
                path: 'delivery-logs',
                canActivate: [roleGuard(AdminRole.Admin, AdminRole.Moderator)],
                loadChildren: () => import('./pages/delivery-logs/delivery-logs.routes'),
            },
            {
                path: 'reports',
                canActivate: [roleGuard(AdminRole.Admin, AdminRole.Moderator)],
                loadChildren: () => import('./pages/reports/reports.routes'),
            },
            {
                path: 'background-checks',
                canActivate: [roleGuard(AdminRole.Admin, AdminRole.Moderator), countryFeatureGuard('backgroundChecks')],
                loadChildren: () => import('./pages/background-checks/background-checks.routes'),
            },
            {
                path: 'phone-verifications',
                canActivate: [roleGuard(AdminRole.Admin, AdminRole.Moderator)],
                loadChildren: () => import('./pages/phone-verifications/phone-verifications.routes'),
            },
            {
                path: 'phone-number-logs',
                canActivate: [roleGuard(AdminRole.Admin, AdminRole.Moderator)],
                loadChildren: () => import('./pages/phone-number-logs/phone-number-logs.routes'),
            },
            {
                path: 'phone-access-logs',
                canActivate: [roleGuard(AdminRole.Admin, AdminRole.Moderator)],
                loadChildren: () => import('./pages/phone-access-logs/phone-access-logs.routes'),
            },
            {
                path: 'customer-complaints',
                canActivate: [roleGuard(AdminRole.Admin, AdminRole.Moderator)],
                loadChildren: () => import('./pages/customer-complaints/customer-complaints.routes'),
            },
            {
                path: 'honest-reviews',
                canActivate: [roleGuard(AdminRole.Admin, AdminRole.Moderator)],
                loadChildren: () => import('./pages/honest-reviews/honest-reviews.routes'),
            },
            {
                // The UserReviews support inbox behind the four public request forms — a different
                // family from the member-to-member honest reviews above it.
                path: 'classic-reviews',
                canActivate: [roleGuard(AdminRole.Admin, AdminRole.Moderator)],
                loadChildren: () => import('./pages/classic-reviews/classic-reviews.routes'),
            },
            {
                path: 'customer-notes',
                canActivate: [roleGuard(AdminRole.Admin, AdminRole.Moderator)],
                loadChildren: () => import('./pages/customer-notes/customer-notes.routes'),
            },
            {
                // Same gate as the Users group it is listed under; see `app-nav.ts` for why the
                // legacy page's split Administrator/ContentManager vs. +Moderator gate collapses
                // into one here.
                path: 'employment-histories',
                canActivate: [roleGuard(AdminRole.Admin, AdminRole.Moderator)],
                loadChildren: () => import('./pages/employment-histories/employment-histories.routes'),
            },
            {
                path: 'documents',
                canActivate: [roleGuard(AdminRole.Admin, AdminRole.Moderator)],
                loadChildren: () => import('./pages/documents/documents.routes'),
            },
            {
                // A different DVS aggregate from `documents`, not a variant of it: these are the documents
                // customers upload themselves, as sets of pages, which a moderator decides on before any
                // verified document is made from them.
                path: 'user-documents',
                canActivate: [roleGuard(AdminRole.Admin, AdminRole.Moderator)],
                loadChildren: () => import('./pages/user-documents/user-documents.routes'),
            },
            // The queue's address while every page was a file of its own; kept so bookmarks still land.
            { path: 'user-files', redirectTo: 'user-documents', pathMatch: 'full' },
            {
                path: 'photo-moderation',
                canActivate: [roleGuard(AdminRole.Admin, AdminRole.Moderator)],
                loadChildren: () => import('./pages/photo-moderation/photo-moderation.routes'),
            },
            {
                path: 'audio-message-moderation',
                canActivate: [roleGuard(AdminRole.Admin, AdminRole.Moderator)],
                loadChildren: () => import('./pages/audio-message-moderation/audio-message-moderation.routes'),
            },
            {
                path: 'fairy-tale-audios',
                canActivate: [roleGuard(AdminRole.Admin, AdminRole.Moderator)],
                loadChildren: () => import('./pages/fairy-tale-audios/fairy-tale-audios.routes'),
            },
            {
                path: 'resume-moderation',
                canActivate: [roleGuard(AdminRole.Admin, AdminRole.Moderator)],
                loadChildren: () => import('./pages/resume-moderation/resume-moderation.routes'),
            },
            {
                path: 'recommendation-moderation',
                canActivate: [roleGuard(AdminRole.Admin, AdminRole.Moderator)],
                loadChildren: () => import('./pages/recommendation-moderation/recommendation-moderation.routes'),
            },
            {
                path: 'vacancy-moderation',
                canActivate: [roleGuard(AdminRole.Admin, AdminRole.Moderator)],
                loadChildren: () => import('./pages/vacancy-moderation/vacancy-moderation.routes'),
            },
        ],
    },
    {
        path: '**',
        redirectTo: '',
    },
];
